import { useOrderPanelStore } from '../../stores/order-panel-store'
import { calculateArea, isExtraIncludedInPackage } from '../../lib/pricing/vloerkleed'
import type { VloerkleedCartItem, VloerkleedShape } from '../../lib/pricing/types'
import {
  OFFERTE_PRIMARY_PRODUCTS,
  DEFAULT_OFFERTE_PRIMARY_SKU,
  findOffertePrimaryProduct,
} from '../../lib/offerte-products'
import clsx from 'clsx'

const SHAPES: Array<{ value: VloerkleedShape; label: string }> = [
  { value: 'rechthoekig', label: 'Rechthoekig' },
  { value: 'rond', label: 'Rond' },
  { value: 'overig', label: 'Overig' },
]

function ItemSummary({ item }: { item: VloerkleedCartItem }) {
  const { areaM2Rounded } = calculateArea(item)
  const bootstrap = useOrderPanelStore((s) => s.bootstrap)
  const product = findOffertePrimaryProduct(item.productCode)

  if (product.mode === 'count') {
    const aantal = item.aantal ?? 0
    return (
      <span className="text-xs text-slate-500">
        {aantal > 0 ? `${product.label} × ${aantal}` : 'Niet geconfigureerd'}
      </span>
    )
  }

  const pkg = item.selectedPackage
    ? bootstrap?.packages.find((p) => p.sku === item.selectedPackage)
    : null

  const parts: string[] = []
  if (item.shape === 'rechthoekig' && item.lengthCm > 0 && item.widthCm > 0) {
    parts.push(`${(item.lengthCm / 100).toFixed(1)} x ${(item.widthCm / 100).toFixed(1)}m`)
  } else if (item.shape === 'rond' && item.diameterCm > 0) {
    parts.push(`ø${(item.diameterCm / 100).toFixed(1)}m`)
  } else if (areaM2Rounded > 0) {
    parts.push(`${areaM2Rounded}m²`)
  }
  if (pkg) parts.push(pkg.name)

  return (
    <span className="text-xs text-slate-500">
      {parts.length > 0 ? parts.join(' — ') : 'Niet geconfigureerd'}
    </span>
  )
}

function ItemCard({ item, index }: { item: VloerkleedCartItem; index: number }) {
  const updateItem = useOrderPanelStore((s) => s.updateItem)
  const removeItem = useOrderPanelStore((s) => s.removeItem)
  const duplicateItem = useOrderPanelStore((s) => s.duplicateItem)
  const toggleItemExpanded = useOrderPanelStore((s) => s.toggleItemExpanded)
  const bootstrap = useOrderPanelStore((s) => s.bootstrap)
  const items = useOrderPanelStore((s) => s.items)

  const product = findOffertePrimaryProduct(item.productCode)
  const currentPrimarySku = item.productCode ?? DEFAULT_OFFERTE_PRIMARY_SKU

  // Packages en extras filteren op Gerelateerd_hoofdproduct__c = current primary.
  // Voor count-based products tonen we ze sowieso niet (mode-gate hieronder).
  const packages = (bootstrap?.packages || []).filter(
    (p) => p.relatedProductCode === currentPrimarySku,
  )
  const extras = (bootstrap?.availableProducts || []).filter(
    (p) => p.isExtra && p.relatedProductCode === currentPrimarySku,
  )

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      {/* Header — always visible */}
      <button
        onClick={() => toggleItemExpanded(item.id)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-slate-700">{product.label} {index + 1}</span>
          <ItemSummary item={item} />
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={(e) => { e.stopPropagation(); duplicateItem(item.id) }}
            className="p-1.5 text-slate-400 hover:text-indigo-600 transition-colors"
            title="Kopiëren"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          {items.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); removeItem(item.id) }}
              className="p-1.5 text-slate-400 hover:text-red-500 transition-colors"
              title="Verwijderen"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          )}
          <svg
            className={clsx('w-4 h-4 text-slate-400 transition-transform', item.isExpanded && 'rotate-180')}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Body — expanded */}
      {item.isExpanded && (
        <div className="px-4 py-4 space-y-4">
          {/* Product (per-item; consistent met NewOfferteDialog) */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Product</label>
            <select
              value={item.productCode ?? DEFAULT_OFFERTE_PRIMARY_SKU}
              onChange={(e) => {
                const newSku = e.target.value
                const newProduct = findOffertePrimaryProduct(newSku)
                // Reset pakket/extras die niet bij het nieuwe product passen
                const newPackages = (bootstrap?.packages || []).filter(
                  (p) => p.relatedProductCode === newSku,
                )
                const newExtras = (bootstrap?.availableProducts || []).filter(
                  (p) => p.isExtra && p.relatedProductCode === newSku,
                )
                const validPackageSkus = new Set(newPackages.map((p) => p.sku))
                const validExtraSkus = new Set(newExtras.map((p) => p.sku))
                updateItem(item.id, {
                  productCode: newSku,
                  selectedPackage:
                    item.selectedPackage && validPackageSkus.has(item.selectedPackage)
                      ? item.selectedPackage
                      : null,
                  extras: item.extras.filter((e) => validExtraSkus.has(e)),
                  // Bij overstap naar count-based: zet aantal default 1 als nog niet gezet
                  ...(newProduct.mode === 'count' && (item.aantal ?? 0) <= 0
                    ? { aantal: 1 }
                    : {}),
                })
              }}
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
            >
              {OFFERTE_PRIMARY_PRODUCTS.map((p) => (
                <option key={p.sku} value={p.sku}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* Count-based body (Kussen) */}
          {product.mode === 'count' && (
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Aantal (stuks)</label>
              <input
                type="number"
                min={1}
                value={item.aantal ?? 1}
                onChange={(e) => updateItem(item.id, { aantal: Math.max(1, parseInt(e.target.value) || 0) })}
                className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          )}

          {/* Shape-based body (Vloerkleed / Wederverkoper / Impregneren) */}
          {product.mode === 'shape' && (
            <>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Vorm</label>
                <div className="flex gap-2">
                  {SHAPES.map((s) => (
                    <button
                      key={s.value}
                      onClick={() => updateItem(item.id, { shape: s.value })}
                      className={clsx(
                        'px-3 py-1.5 text-sm rounded-md border transition-colors',
                        item.shape === s.value
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      )}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 mb-1.5 block">Afmetingen</label>
                {item.shape === 'rechthoekig' && (
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <input
                        type="number"
                        min={10}
                        max={2000}
                        placeholder="Lengte (cm)"
                        value={item.lengthCm || ''}
                        onChange={(e) => updateItem(item.id, { lengthCm: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    <span className="self-center text-slate-400">x</span>
                    <div className="flex-1">
                      <input
                        type="number"
                        min={10}
                        max={2000}
                        placeholder="Breedte (cm)"
                        value={item.widthCm || ''}
                        onChange={(e) => updateItem(item.id, { widthCm: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
                {item.shape === 'rond' && (
                  <input
                    type="number"
                    min={10}
                    max={2000}
                    placeholder="Diameter (cm)"
                    value={item.diameterCm || ''}
                    onChange={(e) => updateItem(item.id, { diameterCm: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                )}
                {item.shape === 'overig' && (
                  <input
                    type="number"
                    min={0.1}
                    step={0.01}
                    placeholder="Oppervlakte (m²)"
                    value={item.areaM2Manual || ''}
                    onChange={(e) => updateItem(item.id, { areaM2Manual: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                )}
              </div>

              {product.hasPackages && packages.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Pakket</label>
                  <div className="space-y-2">
                    {packages.map((pkg) => (
                      <button
                        key={pkg.sku}
                        onClick={() => updateItem(item.id, {
                          selectedPackage: item.selectedPackage === pkg.sku ? null : pkg.sku,
                        })}
                        className={clsx(
                          'w-full text-left px-3 py-2.5 rounded-md border transition-colors',
                          item.selectedPackage === pkg.sku
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-slate-200 hover:border-slate-300'
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <span className={clsx(
                            'text-sm font-medium',
                            item.selectedPackage === pkg.sku ? 'text-indigo-700' : 'text-slate-700'
                          )}>
                            {pkg.name}
                          </span>
                          {pkg.percentageMultiplier > 0 && (
                            <span className="text-xs text-slate-500">+{pkg.percentageMultiplier}%</span>
                          )}
                        </div>
                        {pkg.features.length > 0 && (
                          <p className="text-xs text-slate-500 mt-0.5">
                            {pkg.features.join(', ')}
                          </p>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

            </>
          )}

          {/* Extras — gemeenschappelijk voor shape én count modes (filter op relatedProductCode) */}
          {extras.length > 0 && (
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Extra's</label>
              <div className="space-y-1.5">
                {extras.map((extra) => {
                  const included = isExtraIncludedInPackage(extra.sku, item.selectedPackage, extra.name)
                  const selected = item.extras.includes(extra.sku)

                  return (
                    <label
                      key={extra.sku}
                      className={clsx(
                        'flex items-center gap-2.5 px-3 py-2 rounded-md border cursor-pointer transition-colors',
                        included
                          ? 'border-emerald-200 bg-emerald-50'
                          : selected
                          ? 'border-indigo-300 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      )}
                    >
                      <input
                        type="checkbox"
                        disabled={included}
                        checked={selected || included}
                        onChange={() => {
                          if (included) return
                          const newExtras = selected
                            ? item.extras.filter((e) => e !== extra.sku)
                            : [...item.extras, extra.sku]
                          updateItem(item.id, { extras: newExtras })
                        }}
                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-slate-700 flex-1">{extra.name}</span>
                      {included && (
                        <span className="text-xs text-emerald-600">(inbegrepen)</span>
                      )}
                    </label>
                  )
                })}
              </div>
            </div>
          )}

          {/* Notes — gemeenschappelijk voor beide modes */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1.5 block">Bijzonderheden</label>
            <input
              type="text"
              placeholder="Bijv. vlekken, type kleed..."
              value={item.notes}
              onChange={(e) => updateItem(item.id, { notes: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
        </div>
      )}
    </div>
  )
}

export function VloerkleedConfigurator() {
  const items = useOrderPanelStore((s) => s.items)
  const addItem = useOrderPanelStore((s) => s.addItem)

  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <ItemCard key={item.id} item={item} index={idx} />
      ))}
      <button
        onClick={addItem}
        className="w-full py-2 border-2 border-dashed border-slate-300 rounded-lg text-sm text-slate-500 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
      >
        + Item toevoegen
      </button>
    </div>
  )
}
